import { useCallback, useEffect, useState } from 'react';
import type { Show } from '@liveforge/shared';
import { errorMessage } from '../lib/api';
import { getShow, listShows } from '../lib/resources';

export interface ShowsResult {
  shows: Show[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Lets a page apply a create/delete result without a round trip. */
  setShows: (shows: Show[]) => void;
}

export function useShows(): ShowsResult {
  const [shows, setShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    listShows(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setShows(data);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(cause));
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { shows, loading, error, reload, setShows };
}

export interface ShowResult {
  show: Show | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setShow: (show: Show) => void;
}

export function useShow(showId: string | undefined): ShowResult {
  const [show, setShow] = useState<Show | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!showId) {
      setLoading(false);
      setError('No show was requested.');
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    getShow(showId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setShow(data);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(cause));
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [showId, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { show, loading, error, reload, setShow };
}
